import { useMyUser } from '@/app/hooks/data-hooks/useMyUser'
import { ROUTES } from '@/app/lib/constants'
import { isBoom } from '@hapi/boom'
import { useRouter } from 'next/router'
import React, { useMemo } from 'react'

type AuthContextValue = {
  isAuthenticated: boolean
  userId: string | null
  firstName: string | null
  lastName: string | null
  isLoading: boolean
  company: string | null
  roles: string[] | null
}

const AuthContext = React.createContext<AuthContextValue>({
  isAuthenticated: false,
  userId: null,
  firstName: null,
  lastName: null,
  isLoading: false,
  company: null,
  roles: null,
})

type AuthProviderProps = {
  children: React.ReactNode
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const router = useRouter()

  const { data: { data: user } = {}, isLoading } = useMyUser({
    options: {
      enabled:
        router.isReady &&
        [ROUTES.authLogin, ROUTES.logout].every(
          (route) => !router.pathname.includes(route)
        ),
      retry: (failureCount, error) => {
        if (isBoom(error, 401)) {
          return false
        }
        return failureCount < 3
      },
    },
  })

  const memoizedValue = useMemo(() => {
    return {
      isAuthenticated: !!user,
      firstName: user?.firstName ?? null,
      lastName: user?.lastName ?? null,
      userId: user?._id ?? null,
      company: (user?.company as string) ?? null,
      roles: user?.roles ?? [],
      isLoading,
    }
  }, [user, isLoading])

  return (
    <AuthContext.Provider value={memoizedValue}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = React.useContext(AuthContext)

  return context
}

export function useIsAuthenticated() {
  const context = useAuth()
  return context.isAuthenticated
}
